// Catalog of voice commands grouped by page/extension for the "What can I say?" UI
// Keep examples concise and user-friendly; do not include technical details
// This file now uses translation keys from translation-en.json

// Helper function to build catalog from translations
const buildCatalogFromTranslations = (t) => {
  // Since keySeparator is false, we need to access nested objects directly
  const getTranslation = (path) => {
    try {
      // Access the nested object structure directly
      const voiceCommands = t("voiceCommands", { returnObjects: true });
      if (!voiceCommands) return null;
      
      // Navigate through the path (e.g., "attendance.tabSwitch")
      const parts = path.split(".");
      let result = voiceCommands;
      for (const part of parts) {
        if (result && typeof result === "object" && part in result) {
          result = result[part];
        } else {
          return null;
        }
      }
      return result;
    } catch (error) {
      console.warn(`Error getting translation for path: ${path}`, error);
      return null;
    }
  };

  const buildSection = (sectionKey, sectionConfig) => {
    const section = getTranslation(sectionConfig.path);
    if (!section || !section.title) return null;

    const items = [];
    if (section.items) {
      Object.keys(section.items).forEach((itemKey) => {
        const item = section.items[itemKey];
        if (item && item.example && item.description) {
          items.push({
            example: item.example,
            description: item.description,
          });
        }
      });
    }

    return {
      title: section.title,
      items: items,
    };
  };

  return {
    attendance: [
      buildSection("attendance.tabSwitch", { path: "attendance.tabSwitch" }),
      buildSection("attendance.lessonSwitch", { path: "attendance.lessonSwitch" }),
      buildSection("attendance.presentAbsentLate", { path: "attendance.presentAbsentLate" }),
      buildSection("attendance.notes", { path: "attendance.notes" }),
      buildSection("attendance.undoRedo", { path: "attendance.undoRedo" }),
      buildSection("attendance.cleanInput", { path: "attendance.cleanInput" }),
      buildSection("attendance.confirm", { path: "attendance.confirm" }),
      buildSection("attendance.export", { path: "attendance.export" }),
      buildSection("attendance.help", { path: "attendance.help" }),
    ].filter(Boolean),
    whiteboard: [
      buildSection("whiteboard.lessonChange", { path: "whiteboard.lessonChange" }),
      buildSection("whiteboard.generalControls", { path: "whiteboard.generalControls" }),
      buildSection("whiteboard.drawingActions", { path: "whiteboard.drawingActions" }),
      buildSection("whiteboard.frames", { path: "whiteboard.frames" }),
      buildSection("whiteboard.textFormula", { path: "whiteboard.textFormula" }),
      buildSection("whiteboard.background", { path: "whiteboard.background" }),
      buildSection("whiteboard.collection", { path: "whiteboard.collection" }),
      buildSection("whiteboard.libraryOpenClose", { path: "whiteboard.libraryOpenClose" }),
      buildSection("whiteboard.libraryTopTabs", { path: "whiteboard.libraryTopTabs" }),
      buildSection("whiteboard.libraryCategories", { path: "whiteboard.libraryCategories" }),
      buildSection("whiteboard.useTemplate", { path: "whiteboard.useTemplate" }),
      buildSection("whiteboard.pageAddSwitch", { path: "whiteboard.pageAddSwitch" }),
      buildSection("whiteboard.exportPagesFile", { path: "whiteboard.exportPagesFile" }),
      buildSection("whiteboard.audioVideo", { path: "whiteboard.audioVideo" }),
      buildSection("whiteboard.spinner", { path: "whiteboard.spinner" }),
      buildSection("whiteboard.lessonGenerate", { path: "whiteboard.lessonGenerate" }),
      buildSection("whiteboard.chartGenerate", { path: "whiteboard.chartGenerate" }),
      buildSection("whiteboard.help", { path: "whiteboard.help" }),
    ].filter(Boolean),
    navigation: [
      buildSection("navigation.mainTabs", { path: "navigation.mainTabs" }),
    ].filter(Boolean),
  };
};

// Fallback catalog for when translations are not available
export const COMMAND_CATALOG_FALLBACK = {
  attendance: [
    {
      title: "Tab Switch",
      items: [
        {
          example: "Go to attendance",
          description: "Switch to Attendance tab",
        },
        { example: "Open whiteboard", description: "Switch to Whiteboard tab" },
        {
          example: "Open exam dashboard",
          description: "Switch to Exam Dashboard tab",
        },
      ],
    },
    {
      title: "Lesson Switch",
      items: [
        {
          example: "Go to lesson Algebra",
          description: "Open a lesson by name",
        },
        { example: "Next lesson", description: "Go to next lesson" },
        { example: "Previous lesson", description: "Go to previous lesson" },
      ],
    },
    {
      title: "Present / Absent / Late",
      items: [
        { example: "Mark John present", description: "Set a student present" },
        { example: "Mark Sara absent", description: "Set a student absent" },
        { example: "Mark John absent due to sickness", description: "Set a student absent with a reason" },
        { example: "Mark Omar late", description: "Set a student late" },
        { example: "Mark all present", description: "Set everyone present" },
        { example: "Mark all absent", description: "Set everyone absent" },
        { example: "Mark all late", description: "Set everyone late" },
      ],
    },
    {
      title: "Notes",
      items: [
        {
          example: "Add note for John: excellent participation",
          description: "Create a note",
        },
        {
          example: "Update note for Sara: needs help in algebra",
          description: "Update a note",
        },
        { example: "Clear note for Omar", description: "Remove a note" },
      ],
    },
    {
      title: "Undo / Redo",
      items: [
        { example: "Undo", description: "Revert the last action" },
        { example: "Roll back", description: "Alternative wording for undo" },
        { example: "Redo", description: "Repeat the last action" },
        { example: "Apply again", description: "Alternative wording for redo" },
      ],
    },
    {
      title: "Clean Input",
      items: [
        { example: "Hi Assistant clean input", description: "Reset current input fields" },
        { example: "Hi Assistant reset", description: "Reset current input fields" },
        { example: "Hi Assistant start over", description: "Reset current input fields" },
        { example: "Hi Assistant refresh", description: "Reset current input fields" },
      ],
    },
    {
      title: "Confirm",
      items: [
        { example: "Hi Assistant confirm", description: "Confirm and execute the pending action" },
        { example: "Hi Assistant go now", description: "Start executing the pending action" },
        { example: "Hi Assistant proceed", description: "Proceed with the pending action" },
        { example: "Hi Assistant do it", description: "Execute the pending action" },
      ],
    },
    {
      title: "Export",
      items: [
        {
          example: "Export attendance report",
          description: "Download attendance data as Excel",
        },
      ],
    },
    {
      title: "Help",
      items: [
        { example: "What can I say", description: "Show voice command help" },
        { example: "Show commands", description: "Show voice command help" },
        { example: "Help", description: "Show voice command help" },
      ],
    },
  ],
  whiteboard: [
    {
      title: "Lesson Change",
      items: [
        {
          example: "Go to lesson Photosynthesis",
          description: "Open a lesson by name",
        },
        { example: "Next lesson", description: "Go to next lesson" },
        { example: "Previous lesson", description: "Go to previous lesson" },
      ],
    },
    {
      title: "General Controls",
      items: [
        { example: "Undo", description: "Revert the last action" },
        { example: "Redo", description: "Repeat the last undone action" },
        {
          example: "Hi Assistant clean input",
          description: "Reset current input in the voice widget",
        },
        {
          example: "Hi Assistant confirm",
          description: "Execute the pending action in the voice widget",
        },
        { example: "Help", description: "Show voice command help" },
      ],
    },
    {
      title: "Drawing Actions",
      items: [
        { example: "Draw rectangle", description: "Draw a rectangle" },
        { example: "Draw diamond", description: "Draw a diamond" },
        { example: "Draw ellipse", description: "Draw a circle/ellipse" },
        { example: "Draw arrow", description: "Draw an arrow" },
        { example: "Draw line", description: "Draw a straight line" },
        {
          example: "Clear canvas",
          description: "Erase everything on the canvas",
        },
      ],
    },
    {
      title: "Frames",
      items: [
        {
          example: "Create frame with rectangle and arrow",
          description: "Create a smart frame with elements",
        },
        {
          example: "Delete frame Introduction",
          description: "Remove a frame by name",
        },
      ],
    },
    {
      title: "Text / Formula",
      items: [
        {
          example: "Insert text Photosynthesis",
          description: "Add text to the canvas",
        },
        { example: "Add formula", description: "Insert a formula" },
      ],
    },
    {
      title: "Background",
      items: [
        {
          example: "Set background to red",
          description: "Change canvas background color",
        },
        {
          example: "Make background transparent",
          description: "Set background to transparent",
        },
        {
          example: "Change background color to #ffffff",
          description: "Set background via hex",
        },
      ],
    },
    {
      title: "Collection",
      items: [
        {
          example: "Add elephent",
          description: "Add an elephent to the canvas from collection library",
        },
        {
          example: "Bring element E1234",
          description: "Insert an item by element code",
        },
      ],
    },
    {
      title: "Library Open/Close",
      items: [
        { example: "Open library", description: "Show the library panel" },
        { example: "Close library", description: "Hide the library panel" },
        {
          example: "Show collections",
          description: "Open collections in library",
        },
        {
          example: "Hide collections",
          description: "Close collections in library",
        },
      ],
    },
    {
      title: "Library Top Tabs",
      items: [
        {
          example: "Go to My Library",
          description: "Switch top tab to My Library",
        },
        {
          example: "Open Collections",
          description: "Switch top tab to Collections",
        },
      ],
    },
    {
      title: "Library Categories",
      items: [
        { example: "Open Template", description: "Switch sidebar to Template" },
        {
          example: "Go to Stock Image",
          description: "Switch sidebar to Stock Image",
        },
        {
          example: "Open My Collection",
          description: "Switch sidebar to My Collection",
        },
        { example: "Open Images", description: "Switch sidebar to Images" },
        { example: "Open Videos", description: "Switch sidebar to Videos" },
        { example: "Open Audios", description: "Switch sidebar to Audios" },
        {
          example: "Open Under Review",
          description: "Switch sidebar to Under Review",
        },
        { example: "Open Liked", description: "Switch sidebar to Liked" },
        { example: "Open Bookmark", description: "Switch sidebar to Bookmark" },
      ],
    },
    {
      title: "Use Template",
      items: [
        {
          example: "Use template Timeline",
          description: "Insert a template by name",
        },
        {
          example: "Add template Brainstorming",
          description: "Insert a template by name",
        },
      ],
    },
    {
      title: "Page — Add / Switch",
      items: [
        {
          example: "Add new page",
          description: "Create a new whiteboard page",
        },
        {
          example: "Create a page",
          description: "Create a new whiteboard page",
        },
        {
          example: "Go to page Introduction",
          description: "Switch to a page by name",
        },
        { example: "Go to page 3", description: "Switch to a page by number" },
      ],
    },
    {
      title: "Export Pages / File",
      items: [
        {
          example: "Export current page as image",
          description: "Export current page to image",
        },
        {
          example: "Export all pages as file",
          description: "Export all pages to JSON",
        },
        {
          example: "Open export dialog",
          description: "Open the export dialog",
        },
      ],
    },
    {
      title: "Audio / Video",
      items: [
        {
          example: "Insert audio of animal sounds",
          description: "Add audio by query",
        },
        {
          example: "Insert video of photosynthesis",
          description: "Add local video by query",
        },
      ],
    },
    {
      title: "Spinner",
      items: [
        {
          example: "Generate spinwheel with topic Food",
          description: "Add a spin wheel",
        },
      ],
    },
    {
      title: "Lesson Generate",
      items: [
        {
          example: "Generate a lesson plan on fractions",
          description: "Create a lesson plan",
        },
      ],
    },
    {
      title: "Chart Generate",
      items: [
        {
          example: "Draw a bar chart of test scores",
          description: "Generate a chart",
        },
      ],
    },
    {
      title: "Help",
      items: [
        { example: "What can I say", description: "Show voice command help" },
        { example: "Show commands", description: "Show voice command help" },
        { example: "Help", description: "Show voice command help" },
      ],
    },
  ],
  navigation: [
    {
      title: "Main Tabs",
      items: [
        { example: "Go to attendance", description: "Open Attendance tab" },
        { example: "Open whiteboard", description: "Open Whiteboard tab" },
        { example: "Open exam dashboard", description: "Open Exam Dashboard" },
        { example: "INES, Platform: Events", description: "Navigate directly to Events" },
      ],
    },
  ],
};

export const getCommandCatalogForPage = (page, t = null) => {
  const base = String(page || "attendance").toLowerCase();
  const sections = [];
  
  // Use translations if available, otherwise use fallback
  let catalog;
  if (t) {
    try {
      catalog = buildCatalogFromTranslations(t);
    } catch (error) {
      console.warn("Error building catalog from translations, using fallback:", error);
      catalog = COMMAND_CATALOG_FALLBACK;
    }
  } else {
    catalog = COMMAND_CATALOG_FALLBACK;
  }

  if (base === "attendance") {
    sections.push(...catalog.attendance);
    // Tab switch is already covered in Attendance via "Tab Switch"
  } else if (base === "whiteboard") {
    sections.push(...catalog.whiteboard);
    // Keep navigation out to focus on requested whiteboard categories
  } else {
    // Fallback: show navigation only
    sections.push(...catalog.navigation);
  }
  return sections;
};
